import { Button, Space, Table } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import Page from "../../Common/Page";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent.jsx";
import PermissionCheck from "../../Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useDeleteModal } from "~hooks/useDeleteModal.jsx";
import { useHandlePagination } from "~hooks/useHandlePagination.js";
import { useHttp } from "~hooks/useHttp.js";
import AddStandingsAdjustmentsModal from "./AddStandingsAdjustmentsModal";
import { getColumns } from "./columnsRepository";
import { useNotification } from "~hooks/useNotification";

const ButtonToolbar = styled.div`
  display: flex;
  justify-content: end;
`;

const StandingsAdjustments = () => {
  const { formatMessage } = useIntl();
  const { organizationId, sport, seasonId, customerId } = useParams();
  const { loading, request, refetch } = useHttp();
  const { request: requestFetchSeason } = useHttp();
  const [data, setData] = useState(null);
  const [season, setSeason] = useState(null);
  const [isAddStandingAdjustmentsOpen, setIsAddStandingAdjustmentsOpen] = useState(false);
  const pageRef = useRef();
  const notification = useNotification();

  const {
    loading: createStandingsAdjustmentsLoading,
    error: createStandingsAdjustmentsError,
    request: createStandingsAdjustmentsRequest,
    cleanError: createStandingsAdjustmentsCleanError,
  } = useHttp();

  const fetchSeasonDetails = async () => {
    try {
      const res = await requestFetchSeason({
        url: `/v1/${sport}/o/${organizationId}/seasons/${seasonId}`,
      });
      setSeason(res?.data?.[0]);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchData = useCallback(async () => {
    request({
      url: `/v1/${sport}/o/${organizationId}/seasons/${seasonId}/standings/adjustments?include=seasons,entities&limit=200&offset=0`,
    })
      .then((res) => {
        setData(res);
      })
      .catch((e) => console.error(e));
  }, [request, setData, organizationId, seasonId, sport]);

  const deleteModal = useDeleteModal({
    headingText: formatMessage({ id: "standings.adjustments.delete" }),
    actionText: formatMessage({ id: "delete" }),
    cancelText: formatMessage({ id: "cancel" }),
    text: formatMessage({ id: "standings.adjustments.delete.confirm" }),
    refreshTrigger: fetchData,
    pageRef: pageRef,
  });

  function getTeamName(id) {
    return data?.includes?.resources?.entities[id]?.nameFullLocal;
  }

  function getTeamLink(id) {
    return `/${customerId}/admin/${sport}/${organizationId}/entities/manage/${id}`;
  }

  function getTeamLogo(id) {
    const images = data?.includes?.resources?.entities[id]?.images;
    if (!images || images.length === 0) {
      return null;
    }

    return images.find((x) => x.imageType === "LOGO")?.url;
  }

  useEffect(() => {
    fetchData();
    fetchSeasonDetails();
  }, [fetchData]);

  const handleDeleteIntent = (item) => {
    const URL = `/v1/${sport}/o/${organizationId}/standings/adjustments/${item.standingAdjustmentId}`;
    deleteModal.setData.setUrl(URL);
    deleteModal.setData.setTextContent(formatMessage({ id: "delete.item.confirm" }));
    deleteModal.visibility.open();
  };

  const handleAddStandingAdjustmentsCloseModal = () => setIsAddStandingAdjustmentsOpen((prev) => !prev);

  const handleCreateStandingAdjustmentsRequest = async (form) => {
    try {
      const data = await form.validateFields();
      data.applyToAllStandings = data?.applyToAllStandings || false;

      const res = await createStandingsAdjustmentsRequest({
        url: `/v1/${sport}/o/${organizationId}/seasons/${seasonId}/standings/adjustments`,
        method: "POST",
        body: {
          ...data,
        },
      });

      if (res?.data?.length) {
        fetchData();
        handleAddStandingAdjustmentsCloseModal();
        notification.success({
          message: formatMessage({
            id: "standings.adjustment.created",
            defaultMessage: "Standings Adjustment created successfully",
          }),
          placement: "bottomRight",
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const { pageSize, currentPage, handlePaginationChange } = useHandlePagination();

  useEffect(() => {
    if (createStandingsAdjustmentsError) {
      notification.error({
        message: formatMessage({
          id: "standings.adjustment.add.failed",
          defaultMessage: "Add New Standings Adjustment failed",
        }),
        placement: "bottomRight",
      });
      createStandingsAdjustmentsCleanError();
    }
  }, [createStandingsAdjustmentsCleanError, createStandingsAdjustmentsError]);

  return (
    <Page
      title={[
        season?.nameLocal,
        formatMessage({
          id: "standings.adjustments",
          defaultMessage: "Standings adjustments",
        }),
      ].join(" - ")}
    >
      <PermissionCheck
        permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}
        permissionOptions={[PermissionOperations.DELETE]}
      >
        {deleteModal.element()}
      </PermissionCheck>
      <PermissionCheck
        permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}
        permissionOptions={[PermissionOperations.CREATE]}
      >
        <AddStandingsAdjustmentsModal
          openModal={isAddStandingAdjustmentsOpen}
          handleCloseModal={handleAddStandingAdjustmentsCloseModal}
          handleSendRequest={handleCreateStandingAdjustmentsRequest}
          okButtonLoading={loading}
        />
      </PermissionCheck>
      <Space direction="vertical" style={{ width: "100%" }}>
        <ButtonToolbar>
          <PermissionCheck
            permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}
            permissionOptions={[PermissionOperations.CREATE]}
          >
            <Button
              icon={<i className="mr-1 fas fa-plus"/>}
              type="primary"
              onClick={() => setIsAddStandingAdjustmentsOpen(true)}
            >
              <FormattedMessage id={"standings.adjustment.add"} defaultMessage={"Add new standings adjustment"}/>
            </Button>
          </PermissionCheck>
        </ButtonToolbar>
        <Table
          loading={loading}
          columns={getColumns({
            formatMessage,
            organizationId,
            customerId,
            sport,
            seasonId,
            getTeamName,
            handleDeleteIntent,
            getTeamLink,
            getTeamLogo,
          })}
          dataSource={data?.data}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            position: ["bottomCenter"],
            total: data?.data?.length || 0,
            onChange: handlePaginationChange,
            showTotal: (total, range) => <PaginationComponent pageSize={pageSize} total={total} range={range}/>,
          }}
          size="small"
          scroll={{ x: true }}
        />
      </Space>
    </Page>
  );
};

export default StandingsAdjustments;
